import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UserImg from "../../common/assests/sampleuser.svg";
import ReyLeaf from "../../common/assests/reyleafLogo.svg";
import Paginations from "../../common/pagination/Paginations";
import "./Orders.css";
import { useSelector } from "react-redux";
import { allOrderManagementApi, OrderDetailsApi, orderStatusApi } from "../../api";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import configuration from "../../config/Config";
import { AdminTable, ActionCell } from "../../common/adminTable";

const Orders = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [orderManagementData, setOrderManagementData] = useState([]);
  const [orderDetailsData, setOrderDetailsData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [myTimout, setMyTimeOut] = useState();
  const [status, setStatus] = useState("");
  const [change, setChange] = useState(false);

  const handleOrderManagementApi = async () => {
    try {
      setLoading(true);
      const response = await allOrderManagementApi({ token, searchQuery, currentPage });
      setOrderManagementData(response?.data?.orders || []);
      setCurrentPage(response?.data?.currentPage);
      setTotalPages(response?.data?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error", error);
    }
  };

  useEffect(() => {
    handleOrderManagementApi();
  }, [currentPage, change]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleOrderManagementApi();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(() => handleOrderManagementApi(), 500);
      setMyTimeOut(timeout);
      return () => clearTimeout(timeout);
    }
  }, [searchQuery]);

  const handleOrderDetails = async (id) => {
    try {
      const response = await OrderDetailsApi({ token, id });
      setOrderDetailsData(response);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleStatusUpdate = async (newStatus, id) => {
    const isConfirmed = await confirm({ title: "Update Order Status", description: "Are you sure you want to change this order status?", confirmText: "Update" });
    if (!isConfirmed) return;
    try {
      const response = await orderStatusApi({ token, status: newStatus, id });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occurred" : error?.message);
    }
  };

  const handleClickOpen = (row) => {
    setSelectedRow(row);
    setStatus(row?.status || "");
    setOpen(true);
    handleOrderDetails(row?._id);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      field: "user",
      headerName: "Order By",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={row?.user?.picture ? `${configuration.cdnFront}/${row.user.picture}` : UserImg}
            alt="user"
            style={{ height: 32, width: 32, borderRadius: "50%", flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.user?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "seller",
      headerName: "Business Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={row?.seller?.picture ? `${configuration.cdnFront}/${row.seller.picture}` : ReyLeaf}
            alt="seller"
            style={{ height: 32, width: 32, borderRadius: "50%", flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.seller?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (row) =>
        row?.address ? [row.address.address || ""].filter(Boolean).join(", ") : "-",
    },
    {
      field: "totalAmount",
      headerName: "Total Price",
      width: 110,
      renderCell: (row) => `$${row?.totalAmount ?? "-"}`,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 90,
      renderCell: (row) =>
        row?.items?.reduce((total, item) => total + (item?.quantity || 0), 0),
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 130,
      renderCell: (row) => row?.paymentStatus || "-",
    },
    {
      field: "status",
      headerName: "Order Status",
      width: 110,
      renderCell: (row) => row?.status || "-",
    },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
          ]}
        />
      ),
    },
  ];

  const productColumns = [
    {
      field: "photo",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={
            row?.product?.photos?.length > 0
              ? `${configuration.cdnFront}/${row.product.photos[0].url}`
              : ReyLeaf
          }
          alt="product"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Product Name", minWidth: 140, renderCell: (r) => r?.product?.title || "-" },
    { field: "category", headerName: "Category", width: 120, renderCell: (r) => r?.product?.categoryId?.name || "-" },
    { field: "qty", headerName: "Quantity", width: 80, renderCell: (r) => r?.quantity || "-" },
    { field: "price", headerName: "Regular Price", width: 110, renderCell: (r) => `$${r?.product?.price || "-"}` },
    {
      field: "itemTotal",
      headerName: "Item Total",
      width: 100,
      renderCell: (r) => `$${(r?.quantity * (r?.product?.discountedProce || 0)).toFixed(2)}`,
    },
    { field: "discounted", headerName: "Discounted Price", width: 130, renderCell: (r) => `$${r?.product?.discountedProce || "-"}` },
  ];

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#362B24" }}>
            Order List
          </Typography>
          <TextField
            autoFocus
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            placeholder="Search by order status"
            sx={{
              width: 240,
              height: 48,
              flexShrink: 0,
              borderRadius: "33px",
              background: "#EBEBEB",
              "& .MuiOutlinedInput-root": {
                borderRadius: "33px",
                height: "100%",
                paddingRight: "10px",
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#000", opacity: 0.35 }} />
                </InputAdornment>
              ),
              style: { height: "100%" },
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={orderManagementData}
          loading={loading}
          rowKey="_id"
          emptyText="No orders found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            maxHeight: "70vh",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "720px",
            width: "90%",
            bgcolor: "#F5F5F5",
            borderRadius: "32px",
            boxShadow: 24,
            overflowY: "auto",
            p: 4,
            "&::-webkit-scrollbar": { display: "none" },
            "@media (min-width: 1600px)": { width: "90vw" },
            "@media (max-width: 1400px)": { width: "80vw" },
            "@media (max-width: 1200px)": { width: "60vw" },
            "@media (max-width: 1024px)": { width: "60vw" },
            "@media (max-width: 767px)": { width: "80vw" },
            "@media (max-width: 576px)": { width: "90vw" },
            "@media (max-width: 480px)": { width: "95vw" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography variant="h6" sx={{ marginBottom: "16px", color: "#362B24", fontSize: "20px", fontWeight: "700" }}>
              Order Details
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedRow && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                <Box>
                  <Typography sx={{ fontSize: "15px", fontWeight: "500", color: "#362B24" }}>Order By</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "15px" }}>
                    <img
                      src={selectedRow?.user?.picture ? `${configuration.cdnFront}/${selectedRow.user.picture}` : UserImg}
                      alt="User"
                      style={{ width: "42px", height: "42px", borderRadius: "25px" }}
                    />
                    <strong>{selectedRow?.user?.fullname || "-"}</strong>
                  </Box>
                </Box>
              </Box>
              <br />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "18px" }}>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Order Date</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                    <strong>
                      {selectedRow?.createdAt
                        ? new Date(selectedRow.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                        : "-"}
                    </strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Order ID</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{selectedRow?.orderId || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Quantity</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                    <strong>{selectedRow?.items?.reduce((total, item) => total + (item?.quantity || 0), 0)}</strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24", fontSize: "15px" }}>Payment Status</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{selectedRow?.paymentStatus || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Card</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{selectedRow?.card || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Total Price</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{`$${selectedRow?.totalAmount ?? "-"}`}</strong></Typography>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", marginTop: "40px" }}>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Shipping Address</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                    <strong>
                      {selectedRow?.address
                        ? [selectedRow.address.address || "", selectedRow.address.city || "", selectedRow.address.state || "", selectedRow.address.country || "", selectedRow.address.zipcode || ""].filter(Boolean).join(", ")
                        : "-"}
                    </strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>Seller Name</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "8px" }}>
                    <img
                      src={selectedRow?.seller?.picture ? `${configuration.cdnFront}/${selectedRow.seller.picture}` : ReyLeaf}
                      alt="Seller"
                      style={{ width: "42px", height: "42px", borderRadius: "25px" }}
                    />
                    <strong>{selectedRow?.seller?.fullname || "-"}</strong>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <Typography sx={{ fontWeight: "400", color: "#362B24", marginBottom: { xs: "10px", sm: "0" } }}>
                    Order Status
                  </Typography>
                  <Select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      handleStatusUpdate(e.target.value, selectedRow?._id);
                      handleClose();
                    }}
                    sx={{
                      width: 164,
                      height: 49,
                      borderRadius: 28,
                      border: "1px solid #362B24",
                      marginLeft: { sm: "10px", xs: "0" },
                      color: "#000",
                      fontWeight: "400",
                      display: "block",
                    }}
                    MenuProps={{ PaperProps: { sx: { borderRadius: "10px", border: "1px solid #E8E8E8", backgroundColor: "#EFEFEF" } } }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="declined">Declined</MenuItem>
                    <MenuItem value="packed">Packed</MenuItem>
                    <MenuItem value="dispatched">Dispatched</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </Box>
              </Box>

              <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "20px" }}>
                <AdminTable
                  columns={productColumns}
                  rows={orderDetailsData?.items || []}
                  rowKey="_id"
                  emptyText="No items"
                />
              </Card>
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default Orders;
